import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { sectors, certifications } from "@/data/products";
import { ArrowUpRight, Leaf, FlaskConical, Wheat, Apple, HardHat, Factory, Flower2, Shirt, Cog, UtensilsCrossed, Palette } from "lucide-react";

const iconMap = { Leaf, FlaskConical, Wheat, Apple, HardHat, Factory, Flower2, Shirt, Cog, UtensilsCrossed, Palette };

function Stat({ value, label, sub }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="glass rounded-3xl p-6 md:p-8"
    >
      <div className="font-serif text-4xl md:text-5xl text-[#2C4532]">{value}</div>
      <div className="mt-2 text-xs uppercase tracking-[0.3em] text-[#9F7A4A]">{label}</div>
      {sub && <p className="mt-3 text-sm text-[#4A4D4A] font-light leading-relaxed">{sub}</p>}
    </motion.div>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end pb-20 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1494412651409-8963ce7935a7?auto=format&fit=crop&w=2400&q=90"
            alt="Cinematic global cargo port at golden hour"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 royal-overlay" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D1A12]/85 via-[#16291C]/55 to-[#F9F8F6]" />
          <div className="absolute inset-0 holographic mix-blend-overlay opacity-90" />
          {/* Royal gold lattice */}
          <div className="absolute inset-0 opacity-[0.07]"
               style={{ backgroundImage: "repeating-linear-gradient(45deg,#D4AF37 0,#D4AF37 1px,transparent 1px,transparent 28px)" }} />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <div className="flex items-center gap-4 mb-8">
              <span className="h-2 w-2 rounded-full bg-[#D4AF37] animate-pulse" />
              <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#F9F8F6]/90">
                APEDA Registered · ISO 22000 · GlobalG.A.P. · 40+ Nations
              </span>
            </div>
            <h1 className="font-serif text-[56px] md:text-[112px] leading-[0.95] text-[#F9F8F6] font-light tracking-tighter max-w-5xl">
              Beyond<br /><em className="gold-text not-italic">Boundaries.</em>
            </h1>
            <p className="mt-8 text-base md:text-xl text-[#F9F8F6]/85 max-w-2xl font-light leading-relaxed">
              India's premier multiproduct export house — connecting India's finest farms, quarries, mills and workshops
              with buyers, distributors and retailers across{" "}
              <span className="text-[#D4AF37]">40+ nations</span>. From Basmati rice to Black Galaxy granite, organic fertilisers to Kanjivaram silk.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/catalog" data-testid="hero-cta-portfolio" className="inline-flex items-center gap-3 rounded-full bg-[#F9F8F6] text-[#1A1B1A] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#D4AF37] transition-colors">
                Explore Portfolio <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-quote" className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/60 text-[#F9F8F6] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#D4AF37]/15 transition-colors">
                Request a Quote
              </Link>
            </div>
          </motion.div>

          <div className="mt-16 grid grid-cols-3 gap-4 md:gap-6 max-w-2xl">
            <Stat value="11" label="Export Sectors" />
            <Stat value="500+" label="Product Lines" />
            <Stat value="40+" label="Nations Served" />
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-[#EAE6DF] bg-[#EAE6DF]/40 overflow-hidden py-5">
        <div className="flex gap-12 whitespace-nowrap animate-marquee">
          {[...sectors, ...sectors].map((s, i) => (
            <span key={i} className="text-sm md:text-base tracking-wide text-[#4A4D4A]">
              <span className="text-[#D4AF37] mr-3">◆</span>{s.name}
            </span>
          ))}
        </div>
      </section>

      {/* Why AR Overseas — Company Story */}
      <section className="py-24 md:py-32 bg-[#F9F8F6]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-4">Who We Are</p>
              <h2 className="font-serif text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
                India's trade,<br /><em className="text-[#B85C38]">your advantage.</em>
              </h2>
              <p className="mt-6 text-lg text-[#4A4D4A] font-light leading-relaxed">
                AR Overseas Exports Pvt. Ltd. is headquartered in New Delhi — the heart of India's trading network. We work
                directly with growers, mills, quarry operators, handloom clusters and factories across India, eliminating
                middlemen to deliver genuine value to international buyers.
              </p>
              <p className="mt-4 text-lg text-[#4A4D4A] font-light leading-relaxed">
                Every shipment is backed by independent quality validation, destination-specific compliance documentation and
                the full suite of certificates demanded by modern global trade — from APEDA and Phytosanitary to EU Organic,
                USDA NOP and GCC Halal.
              </p>
              <Link
                to="/about"
                className="mt-8 inline-flex items-center gap-3 text-[#2C4532] text-xs uppercase tracking-[0.25em] hover:text-[#B85C38] transition-colors"
              >
                Our Story <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 gap-4">
              {[
                { v: "Direct", t: "Origin Access", d: "Farm, mill, quarry and workshop direct — no layered intermediaries." },
                { v: "12h", t: "Quote Response", d: "Receive a competitive commercial quotation within 12 business hours." },
                { v: "70%+", t: "Repeat Buyers", d: "Repeat buyers drive the majority of volume — built on trust and reliability." },
                { v: "Full", t: "Documentation", d: "COO, Phytosanitary, COA, BL, Fumigation, MSDS — all in-house." },
              ].map((item) => (
                <motion.div
                  key={item.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="glass rounded-2xl p-6 border border-[#EAE6DF]"
                >
                  <div className="font-serif text-3xl text-[#2C4532]">{item.v}</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.25em] text-[#9F7A4A]">{item.t}</div>
                  <p className="mt-2 text-sm text-[#4A4D4A] font-light leading-relaxed">{item.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-24 md:py-32" id="sectors">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-4">Product Portfolio</p>
              <h2 className="font-serif text-5xl md:text-7xl font-light tracking-tighter leading-[0.95] max-w-2xl">
                What we <em className="text-[#B85C38]">export.</em>
              </h2>
            </div>
            <p className="max-w-md text-base md:text-lg text-[#4A4D4A] font-light leading-relaxed">
              Eleven sectors. 500+ product lines. Every commodity export-ready, certified and documented — from farmgate to destination port.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {sectors.map((s, i) => {
              const Icon = iconMap[s.icon] || Leaf;
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link
                    to={`/sectors/${s.slug}`}
                    data-testid={`sector-card-${s.slug}`}
                    className="group relative block aspect-[3/4] rounded-3xl overflow-hidden border border-[#EAE6DF] bg-[#EAE6DF]"
                  >
                    <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B1A] via-[#1A1B1A]/40 to-transparent" />
                    <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-[#F9F8F6]/90">
                      <span className="text-[10px] uppercase tracking-[0.35em]">Sector {s.index}</span>
                      <span className="h-9 w-9 rounded-full glass-dark flex items-center justify-center"><Icon size={16} /></span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-[#F9F8F6]">
                      <h3 className="font-serif text-3xl md:text-4xl leading-tight">{s.name}</h3>
                      <p className="mt-2 text-sm opacity-80 font-light line-clamp-2">{s.tagline}</p>
                      <div className="mt-4 flex items-center justify-between border-t border-white/20 pt-4">
                        <span className="text-[10px] uppercase tracking-[0.3em] opacity-80">
                          {s.stats.countries} Countries · {s.stats.products} Products
                        </span>
                        <span className="text-[#D4AF37] group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Protocol Timeline */}
      <section className="py-24 md:py-32 bg-[#1A1B1A] text-[#F9F8F6] relative overflow-hidden">
        <div className="absolute inset-0 holographic opacity-40 blur-3xl" />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 relative">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">How it Works</p>
            <h2 className="font-serif text-5xl md:text-7xl font-light tracking-tighter">Export Protocol.</h2>
            <p className="mt-6 text-lg opacity-75 font-light leading-relaxed">
              A single workflow — from request for quotation through container tracking. Full documentation, certifications and destination compliance handled entirely in-house by our expert team.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { n: "01", t: "Submit RFQ", d: "Product specs, destination port, quantity, Incoterm preference. Competitive commercial quotation in 12 hours — guaranteed." },
              { n: "02", t: "Sample Approval", d: "Courier-dispatched pre-shipment samples. SGS / Bureau Veritas third-party inspection available on request." },
              { n: "03", t: "Custom Packaging", d: "Your brand, any-language labels, destination-compliant packaging. FSC and biodegradable options available." },
              { n: "04", t: "Full Documentation", d: "COO · Phytosanitary · COA · BL · Fumigation · MSDS · HACCP — all handled in-house." },
              { n: "05", t: "Shipment & Tracking", d: "Real-time container tracking from Indian port to destination. Post-arrival support and reorder facilitation." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-dark rounded-2xl p-6 border border-white/10"
              >
                <div className="text-[#D4AF37] font-serif text-3xl">{s.n}</div>
                <h4 className="mt-4 font-serif text-2xl">{s.t}</h4>
                <p className="mt-3 text-sm opacity-70 font-light leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-24 md:py-32" id="global">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-4">Trade Network</p>
              <h2 className="font-serif text-5xl md:text-7xl font-light tracking-tighter">Global Reach.<br/>One Source.</h2>
              <p className="mt-6 text-lg text-[#4A4D4A] font-light leading-relaxed">
                Our trade routes span 6 continents. From the ports of Mumbai, JNPT, Mundra and Chennai — we deliver to
                Middle Eastern souks, European supermarkets, African ports and Asian trading hubs. AR Overseas handles
                origin, compliance, logistics and documentation end-to-end.
              </p>
            </div>

            <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { r: "Middle East", v: "88%", d: "UAE · Saudi · Bahrain · Qatar · Kuwait · Oman" },
                { r: "Africa", v: "76%", d: "Algeria · Egypt · Morocco · Nigeria · Senegal" },
                { r: "Europe", v: "65%", d: "Germany · Netherlands · UK · Spain · France" },
                { r: "Asia Pacific", v: "72%", d: "Bangladesh · Sri Lanka · Malaysia · Singapore" },
                { r: "Americas", v: "45%", d: "USA · Canada · Brazil · Colombia" },
                { r: "Oceania & CIS", v: "38%", d: "Australia · Ukraine · Syria · Palestine" },
              ].map((r) => (
                <div key={r.r} className="glass rounded-2xl p-5 border border-[#EAE6DF]">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#9F7A4A]">{r.r}</span>
                    <span className="font-serif text-2xl text-[#2C4532]">{r.v}</span>
                  </div>
                  <p className="mt-3 text-xs text-[#4A4D4A] font-light leading-relaxed">{r.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-16 bg-[#EAE6DF]/50 border-y border-[#EAE6DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-8 text-center">Certified · Compliant · Verified</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {certifications.map((c) => (
              <div key={c.code} className="text-center p-4 rounded-xl bg-white/60 border border-[#EAE6DF]">
                <div className="font-serif text-xl md:text-2xl text-[#2C4532]">{c.code}</div>
                <div className="mt-1 text-[10px] text-[#4A4D4A] uppercase tracking-wider">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="relative rounded-3xl overflow-hidden bg-[#2C4532] p-10 md:p-20 text-[#F9F8F6]">
            <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full holographic blur-3xl opacity-60" />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Start Trading</p>
                <h2 className="font-serif text-5xl md:text-7xl font-light tracking-tighter leading-[0.95]">
                  Let's build something <em className="text-[#D4AF37]">global.</em>
                </h2>
              </div>
              <div>
                <p className="text-base md:text-lg opacity-80 font-light leading-relaxed">
                  Send your requirements and receive a competitive commercial quotation within 12 business hours — with full documentation preview, sample availability and packaging options.
                </p>
                <Link to="/contact" data-testid="home-cta-bottom" className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#D4AF37] text-[#1A1B1A] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#F9F8F6] transition-colors">
                  Transmit Enquiry <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
